import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useI18n } from '../contexts/I18nContext';
import api from '../utils/api';
import { resolveImageUrl } from '../lib/imageUrl';
import { Button } from '../components/ui/button';
import { ShoppingCart, Trash2, Heart } from 'lucide-react';
import { toast } from 'sonner';

const FavoritesPage = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { addToCart } = useCart();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      const response = await api.get('/favorites');
      setFavorites(response.data);
    } catch (error) {
      console.error('Failed to load favorites', error);
      toast.error(t('errorLoadingFavorites'));
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (productId) => {
    try {
      await api.delete(`/favorites/${productId}`);
      setFavorites(favorites.filter((p) => p.id !== productId));
      toast.success(t('removedFromFavorites'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product, 1);
    toast.success(t('addedToCart'));
  };

  return (
    <div className="container mx-auto px-4 lg:px-12 py-8">
      <div className="flex items-center gap-3 mb-8">
        <Heart className="h-8 w-8" />
        <h1 className="text-3xl md:text-4xl font-heading font-bold uppercase" data-testid="favorites-title">
          {t('favorites')}
        </h1>
      </div>

      {loading ? (
        <div className="text-center py-12">{t('loading')}</div>
      ) : favorites.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">{t('noFavorites')}</p>
          <Button onClick={() => navigate('/shop')} data-testid="go-to-shop">
            {t('goToShop')}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {favorites.map((product) => (
            <div
              key={product.id}
              className="product-card bg-white border border-border rounded-md overflow-hidden"
              data-testid={`favorite-card-${product.id}`}
            >
              <div
                className="aspect-square bg-muted cursor-pointer relative"
                onClick={() => navigate(`/product/${product.id}`)}
              >
                <img
                  src={resolveImageUrl(product.image_url)}
                  alt={product.name}
                  className="w-full h-full object-contain p-4 bg-white"
                  loading="lazy"
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFavorite(product.id);
                  }}
                  className="absolute top-2 end-2 bg-white rounded-full p-2 shadow-md hover:bg-red-50"
                  data-testid={`remove-favorite-${product.id}`}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </button>
              </div>

              <div className="p-4">
                <h3
                  className="font-heading font-semibold text-lg mb-2 cursor-pointer hover:text-primary"
                  onClick={() => navigate(`/product/${product.id}`)}
                >
                  {product.name}
                </h3>
                <p className="text-2xl font-bold mb-4">€{product.price.toFixed(2)}</p>

                <Button
                  onClick={() => handleAddToCart(product)}
                  className="w-full"
                  data-testid={`add-to-cart-${product.id}`}
                >
                  <ShoppingCart className="h-4 w-4 me-2" />
                  {t('addToCart')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FavoritesPage;